import * as React from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Link,
    Stack,
    TextField,
    Switch,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    getVirtualAiSettings,
    getVirtualSportSettings,
    updateVirtualAiSettings,
    updateVirtualSportMaxMatchesPerDay,
} from '../../Api';
import { VirtualAiSettingsDto } from '../../models/dtos/VirtualAiSettingsDto';
import { VirtualSportSettingsDto } from '../../models/dtos/VirtualSportSettingsDto';

interface VirtualSettingsDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function VirtualSettingsDialog({ open, onClose }: VirtualSettingsDialogProps) {
    const [settings, setSettings] = React.useState<VirtualAiSettingsDto | null>(null);
    const [sports, setSports] = React.useState<VirtualSportSettingsDto[]>([]);
    const [maxMatchesBySport, setMaxMatchesBySport] = React.useState<Record<number, string>>({});
    const [generateTeamImages, setGenerateTeamImages] = React.useState(true);
    const [generateLeagueImages, setGenerateLeagueImages] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        Promise.all([getVirtualAiSettings(), getVirtualSportSettings()])
            .then(([loadedSettings, loadedSports]) => {
                if (cancelled) return;
                setSettings(loadedSettings);
                setGenerateTeamImages(loadedSettings.generateTeamImages);
                setGenerateLeagueImages(loadedSettings.generateLeagueImages);
                setSports(loadedSports);
                setMaxMatchesBySport(Object.fromEntries(
                    loadedSports.map(sport => [sport.id, String(sport.maxMatchesPerDay)]),
                ));
            })
            .catch(reason => {
                if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load settings');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open]);

    const parsedMaxMatches = (sportId: number) => Number.parseInt(maxMatchesBySport[sportId] ?? '', 10);
    const invalidSportId = sports.find(sport => {
        const value = parsedMaxMatches(sport.id);
        return !Number.isFinite(value) || value < 1;
    })?.id;
    const changedSports = sports.filter(sport => parsedMaxMatches(sport.id) !== sport.maxMatchesPerDay);
    const aiChanged = !settings || (
        generateTeamImages !== settings.generateTeamImages
        || generateLeagueImages !== settings.generateLeagueImages
    );

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            if (aiChanged) {
                const updated = await updateVirtualAiSettings(generateTeamImages, generateLeagueImages);
                setSettings(updated);
                setGenerateTeamImages(updated.generateTeamImages);
                setGenerateLeagueImages(updated.generateLeagueImages);
            }
            if (changedSports.length) {
                const updatedSports = await Promise.all(changedSports.map(sport =>
                    updateVirtualSportMaxMatchesPerDay(sport.id, parsedMaxMatches(sport.id)),
                ));
                const bySportId = new Map(updatedSports.map(sport => [sport.id, sport]));
                setSports(current => current.map(sport => bySportId.get(sport.id) ?? sport));
            }
            onClose();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not save settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SettingsIcon />
                    <span>Virtual sports settings</span>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ py: 7, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <Stack spacing={4}>
                        {error ? <Alert severity="error">{error}</Alert> : null}
                        {sports.length ? (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2 }}>Match scheduling</Typography>
                                <Stack spacing={2}>
                                    {sports.map(sport => {
                                        const value = maxMatchesBySport[sport.id] ?? '';
                                        const parsed = Number.parseInt(value, 10);
                                        const invalid = !Number.isFinite(parsed) || parsed < 1;
                                        return (
                                            <TextField
                                                key={sport.id}
                                                type="number"
                                                fullWidth
                                                label={`${sport.name} — max matches per day`}
                                                value={value}
                                                error={invalid}
                                                helperText={invalid ? 'Must be a whole number of at least 1' : undefined}
                                                inputProps={{ min: 1, step: 1 }}
                                                onChange={event => setMaxMatchesBySport(current => ({
                                                    ...current,
                                                    [sport.id]: event.target.value,
                                                }))}
                                                disabled={saving}
                                            />
                                        );
                                    })}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    An absolute daily ceiling per sport. It outranks league speed and season interval:
                                    both are stretched automatically to stay within it, and the remaining budget is
                                    shared between leagues by their priority.
                                </Typography>
                            </Box>
                        ) : null}

                        {settings ? (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1 }}>AI</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    League, team, player and logo generation run as AI hub tasks: prompts and models are edited in{' '}
                                    <Link component={RouterLink} to="/ai/tasks?ownerService=betup-server-virtual-provider">AI › Tasks</Link>
                                    {' '}(virtual.*), token use and cost are in{' '}
                                    <Link component={RouterLink} to="/ai/usage">AI › Usage</Link>.
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Automatic vector images</Typography>
                                <Stack>
                                    <FormControlLabel
                                        control={<Switch checked={generateTeamImages} onChange={event => setGenerateTeamImages(event.target.checked)} />}
                                        label="Generate logos for newly created teams"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={generateLeagueImages} onChange={event => setGenerateLeagueImages(event.target.checked)} />}
                                        label="Generate logos for newly created leagues"
                                    />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Existing entities can always regenerate their image manually from their details dialog.
                                </Typography>
                            </Box>
                        ) : null}

                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={
                        loading || saving || !settings
                        || invalidSportId !== undefined
                        || (!aiChanged && changedSports.length === 0)
                    }
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
